<template>
    <v-expansion-panels class="mt-3" v-model="contractInfoExpanded">
        <v-expansion-panel>
            <v-expansion-panel-title>
                <div class="d-flex align-center justify-space-between w-100">
                    <span> Informații client </span>
                    <ContractHeader
                        :contract-status="displayContractStatus"
                        :last-history-entry="lastHistoryEntry"
                        :signatureInfo="signatureInfo"
                        :signatureToolTip="signatureToolTip"
                    />
                </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
                <ContractExtraFields
                    :delivered-goods="localDelivered"
                    :advance-invoice-date="localClient.advance_invoice_date"
                    :hasAdvanceInvoice="hasAdvanceInvoice()"
                    @update-delivered-goods="onDeliveredChange"
                    @update-advance-invoice-date="onAdvanceInvoiceDateChange"
                />

                <ContractStatusCheckboxes
                    class="mt-4"
                    :contract-status="client.contract_status"
                    v-model:is-decontare-depusa="isDecontareDepusa"
                    v-model:is-decontat="isDecontat"
                    @update-contract-status="emitUpdatedStatus"
                />
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>

    <v-expansion-panels class="mt-3" v-model="contractExpanded">
        <v-expansion-panel>
            <v-expansion-panel-title
                >Contract și acte adiționale
            </v-expansion-panel-title>
            <v-expansion-panel-text>
                <v-row>
                    <div class="d-flex flex-wrap ga-5 w-100 pa-3">
                        <ContractFields
                            :contract-value="contractValue"
                            :original-contract-value="originalContractValue"
                            :observations="localObservations"
                            :contract-access="contractAccess"
                            :signature-info="signatureInfo"
                            :client="client"
                            :is-loading="isLoading"
                            :is-sending-act-email="isSendingActEmail"
                            @update:contractValue="
                                (val) => (contractValue = val)
                            "
                            @update:observations="emitObservations"
                            @save-contract-value="saveContractValue"
                            @generate-contract="generatePDFContract"
                            @trigger-send-email="sendEmail"
                            @add-new-act="handleAddAct"
                        />
                    </div>
                </v-row>
                <v-row v-if="showAdditionalActsManager">
                    <AdditionalActsManager
                        :acts="additionalActs"
                        @generate-act="generateActAditional"
                        @send-act="sendAdditionalActEmail"
                        @update-act-mods="updateActMods"
                        @delete-act="deleteAct"
                    />
                </v-row>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import {
    createPDFContract,
    showAlertModal,
    createPDFActAditional,
} from "@/utils/utils";
import apiClient from "@/utils/apiClient";
import { mapGetters } from "vuex";
import ContractFields from "@/components/contractStructure/ContractFields.vue";
import ContractHeader from "@/components/contractStructure/ContractHeader.vue";
import ContractStatusCheckboxes from "@/components/contractStructure/ContractStatusCheckboxes.vue";
import AdditionalActsManager from "@/components/contractStructure/AdditionalActsManager.vue";
import { cloneDeep } from "lodash";
import ContractExtraFields from "@/components/contractStructure/ContractExtraFields.vue";

export default {
    name: "ClientContract",
    emits: [
        "update-contract-status",
        "update-delivered-goods",
        "trigger-send-email",
        "update-observations",
        "signature-info",
        "is-contract-sent",
        "update-advance-invoice-date",
    ],
    components: {
        ContractFields,
        ContractHeader,
        ContractStatusCheckboxes,
        AdditionalActsManager,
        ContractExtraFields,
    },
    data() {
        return {
            showConfirmAdvanceInvoiceDialog: false,
            pendingAdvanceInvoiceDate: null,
            additionalActs: [], // what the user sees
            pendingAdds: [], // brand-new acts to POST
            pendingUpdates: [], // existing acts to PATCH
            pendingDeletes: [], // acts to DELETE
            showAdditionalActsManager: false,
            isSendingActEmail: false,
            localObservations: "",
            requiredFieldsErrors: {},
            isClientUpdated: true,
            isLoading: false,
            contractValue: null,
            originalContractValue: null,
            contractAccess: {
                is_signed: false,
                is_accessed: false,
            },
            previousStatus: "",
            isDecontareDepusa: false,
            isDecontat: false,
            localClient: JSON.parse(JSON.stringify(this.client)),
            isFieldMissing: false,
            localDelivered: this.client.delivered_goods ?? false,
            signatureInfo: "",
            signatureToolTip: "",
            actSnapshot: {},
            pendingDiffs: {},
            originalClient: {},
            contractExpanded: [],
            contractInfoExpanded:[0],
            IGNORED_DIFF_KEYS: [
                "latest_additional_act",
                "field_history",
                "contract_access",
                "latest_delivery_request",
                "delivered_goods",
                "approved_contract",
                "contract_status",
                "attachments",
                "client_discount",
                "arthur_commission",
                "agent_commission",
                "structure_type",
                "inverter_user",
                "inverter_user_password",
                "agent_full_name",
                "agent",
                "application_date",
                "cf_date",
                "identification_expiration_date",
                "fiscal_certificate_local_date",
                "fiscal_certificate_state_date",
                "collective_land_registry_date",
                "approved_contract",
                "file_approval_date",
                "additional_email",
                "additional_phone",
                "advance_invoice_date",
                "observations"
            ],
        };
    },
    props: {
        client: {
            type: Object,
        },

        statusHistory: {
            type: Array,
            default: () => [],
        },
    },

    computed: {
        ...mapGetters(["userPermissions"]),
        /**
         * Returns the display status, showing only "Semnat" if it contains signing details.
         * @returns {string}
         */
        displayContractStatus() {
            const status = this.client?.contract_status || null;

            // If current status contains "Semnat" or "Nesemnat"
            const isSignatureStatus =
                status?.toLowerCase().includes("semnat") ||
                status?.toLowerCase() === "nesemnat";

            if (isSignatureStatus && this.statusHistory.length > 0) {
                const sortedHistory = [...this.statusHistory]
                    .filter(
                        (entry) =>
                            entry.field_name === "contract_status" &&
                            !entry.new_value.toLowerCase().includes("semnat") &&
                            entry.new_value.toLowerCase() !== "nesemnat"
                    )
                    .sort(
                        (a, b) =>
                            new Date(b.changed_at) - new Date(a.changed_at)
                    );

                // If there is a previous status that is not "signed" or "unsigned"
                if (sortedHistory.length > 0) {
                    return sortedHistory[0].new_value;
                }
            }

            return status;
        },

        lastHistoryEntry() {
            if (!this.statusHistory.length) return null;

            // Sort by `changed_at` (latest first) to ensure we get the most recent change
            const sortedHistory = [...this.statusHistory].sort(
                (a, b) => new Date(a.changed_at) - new Date(b.changed_at)
            );

            return sortedHistory[sortedHistory.length - 1]; // Get the latest entry
        },
        statusColor() {
            const status = String(this.client.contract_status || ""); // Ensure it's a string

            if (
                [
                    "Factură avans achitată",
                    "Certificat de racordare",
                    "Factură finală",
                    "Decontare depusă",
                    "Decontat",
                ].includes(status)
            ) {
                return "orange";
            }

            switch (status) {
                case "Aprobat":
                    return "purple";
                case "Trimis":
                    return "blue";

                default:
                    return "gray";
            }
        },
    },
    async mounted() {
        if (this.client) {
            this.originalClient = cloneDeep(this.client);
            if (this.client?.observations) {
                this.localObservations = this.client.observations;
            }
            setTimeout(async () => {
                // Fetch ContractAccess data
                await this.fetchContractAccess();
                await this.checkContractToken(); //  Check if contract link is expired
                await this.fetchAdditionalActs();
            }, 250);
        }
    },
    methods: {
        onAdvanceInvoiceDateChange(newDate) {
            this.localClient.advance_invoice_date = newDate;
            this.$emit("update-advance-invoice-date", newDate);
        },

        hasPendingActChanges() {
            return (
                this.pendingAdds.length ||
                this.pendingUpdates.length ||
                this.pendingDeletes.length
            );
        },
        /**
         * Compute a simple key→{old,new} diff
         */
        getDifferences(oldObj, newObj) {
            const diff = {};
            for (const key of Object.keys(newObj)) {
                if (
                    JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])
                ) {
                    diff[key] = { old: oldObj[key], new: newObj[key] };
                }
            }
            return diff;
        },
        /**
         * Create (or merge into) the unsent draft in the child,
         * but do NOT call the network yet.
         */
        createOrMergeDraftActLocal(diffs) {
            let draft = this.additionalActs.find((a) => !a.is_sent);

            if (!draft) {
                draft = {
                    id: null, // marker for “not yet persisted”
                    modifications: {},
                    autoDiffs: {},
                    user_note: "",
                    is_sent: false,
                };
                this.additionalActs.unshift(draft);
                this.showAdditionalActsManager = true;
            }

            Object.assign(draft.modifications, diffs);
        },
        /**
         * Flush your accumulated diffs up to the server, after client‐save.
         */
        async flushPendingActs() {
            if (!this.hasPendingActChanges()) return;
            // 1) Create all new acts
            for (const draft of this.pendingAdds) {
                const payload = {
                    client: this.client.id,
                    contract: this.contractAccess.id,
                    modifications: draft.modifications,
                    auto_modifications: draft.autoDiffs,
                    user_note: draft.user_note || "",
                };

                const { data } = await apiClient.post(
                    "/api/additional-acts/",
                    payload
                );

                Object.assign(draft, data);

                draft.autoDiffs = data.auto_modifications || {};
            }
            this.pendingAdds = [];

            // 2) Update all existing acts
            for (const act of this.pendingUpdates) {
                await apiClient.patch(`/api/additional-acts/${act.id}/`, {
                    modifications: act.modifications,
                });
            }
            this.pendingUpdates = [];

            // 3) Delete all scheduled removals
            for (const actId of this.pendingDeletes) {
                await apiClient.delete(`/api/additional-acts/${actId}/`);
            }
            this.pendingDeletes = [];

            showAlertModal(
                this.$store,
                "Actele adiționale au fost salvate cu succes!",
                "success",
                3000
            );
        },
        deleteAct(act) {
            // Remove from UI immediately
            this.additionalActs = this.additionalActs.filter((a) => a !== act);

            // If it's already on the server, remember its ID
            if (act.id) {
                this.pendingDeletes.push(act.id);
            }

            // And make sure we don't try to re-add or re-update it
            this.pendingAdds = this.pendingAdds.filter((a) => a !== act);
            this.pendingUpdates = this.pendingUpdates.filter((a) => a !== act);
        },
        async fetchAdditionalActs() {
            try {
                const response = await apiClient.get("/api/additional-acts/", {
                    params: { client_id: this.client.id },
                });

                this.additionalActs = response.data.map((act) => ({
                    ...act,
                    autoDiffs: act.auto_modifications || {},
                    loadingGenerate: false,
                    loadingSend: false,
                }));
                this.showAdditionalActsManager = this.additionalActs.length > 0;
            } catch (error) {
                console.error("Failed to fetch additional acts", error);
                showAlertModal(
                    this.$store,
                    "Eroare la încărcarea actelor adiționale.",
                    "danger",
                    5000
                );
            }
        },
        updateActMods(act) {
            // only mark it for PATCH, don’t call apiClient.patch here
            if (act.id) {
                if (!this.pendingUpdates.includes(act)) {
                    this.pendingUpdates.push(act);
                }
            } else {
                // draft has no id yet, it’s in pendingAdds
            }
        },
        /**
         * Adds a new empty additional act entry.
         */
        handleAddAct() {
            const draft = {
                id: null,
                modifications: [],
                autoDiffs: {},
                user_note: "",
                is_sent: false,
            };
            this.additionalActs.unshift(draft);
            this.pendingAdds.push(draft);
            this.showAdditionalActsManager = true;
            return draft;
        },
        async sendAdditionalActEmail(act) {
            try {
                act.loadingSend = true;
                const response = await apiClient.post(
                    "/api/pdf/send_additional_act_email/",
                    {
                        act_id: act.id,
                        email: this.client.email,
                        additionalEmail: this.client.additional_email,
                    }
                );

                if (response.data.status === "success") {
                    act.is_sent = true;
                    showAlertModal(
                        this.$store,
                        "Actul adițional a fost trimis cu succes!",
                        "success",
                        5000
                    );
                } else {
                    showAlertModal(
                        this.$store,
                        response.data.message || "Trimiterea a eșuat.",
                        "danger",
                        6000
                    );
                }
            } catch (error) {
                showAlertModal(
                    this.$store,
                    "Eroare la trimiterea emailului actului.",
                    "danger",
                    6000
                );
            } finally {
                act.loadingSend = false;
            }
        },

        generateActAditional(act) {
            createPDFActAditional(act.id);
        },
        /**
         * Emits the updated observations field to the parent.
         * @param {string} value - New observations text from the textarea.
         */
        emitObservations() {
            this.$emit("update-observations", this.localObservations);
        },

        hasAdvanceInvoice() {
            return this.client.attachments?.some(
                (att) => att.document_type === "advance_invoice"
            );
        },
        /**
         * Emit the current checkbox value (true or false) to parent.
         *
         * @method onDeliveredChange
         * @emits update:delivered_goods {boolean} New state of delivered_goods
         */
        onDeliveredChange() {
            this.$emit("update-delivered-goods", this.localDelivered);
        },

        /**
         * Converts a date to ISO format if it exists.
         * @param {string | Date | null} date - The date to convert.
         * @returns {string | null} - The ISO string or null if the date is invalid.
         */
        toISODate(date) {
            if (!date) return null;
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${year}-${month}-${day}`;
        },
        /**
         * Checks the validity of the contract token by making an API request.
         * If the token is invalid or expired, it updates the status accordingly.
         *
         * @async
         * @returns {Promise<void>} Resolves when the check is complete.
         */
        async checkContractToken() {
            if (
                !this.client ||
                !this.contractAccess ||
                !this.contractAccess.token ||
                this.contractAccess.is_signed
            ) {
                return;
            }

            try {
                // Replaced error interpretation with status response handling
                const res = await apiClient.get("/api/verify-token/", {
                    params: { token: this.contractAccess.token },
                });

                const tokenStatus = res.data.status;

                if (
                    tokenStatus === "expired" &&
                    !this.contractAccess.is_signed
                ) {
                    this.signatureInfo = "Contract Nesemnat";
                }
            } catch (error) {
                console.error(
                    "Unexpected error checking contract token:",
                    error
                );
            }
        },

        /**
         * Emits the updated contract status to the parent.
         * @param {string} newStatus - The new contract status.
         */
        emitUpdatedStatus(newStatus) {
            this.$emit("update-contract-status", newStatus);
        },

        /**
         * Handles contract status updates for:
         * - Aprobat (Approved)
         * - Decontare depusă (Settlement submitted)
         * - Decontat (Settled)
         */
        updateStatus(statusType) {
            switch (statusType) {
                case "Decontare depusă":
                    if (this.isDecontareDepusa) {
                        this.emitUpdatedStatus("Decontare depusă");
                    }
                    break;

                case "Decontat":
                    if (this.isDecontat) {
                        this.emitUpdatedStatus("Decontat");
                    }
                    break;

                default:
                    console.warn("Invalid status type:", statusType);
            }
        },

        /**
         * Fetches the contract access data for the current client by making an API call.
         * If the data is found, initializes local state with the contract value and sign status.
         * Otherwise, it resets local data to null.
         *
         * @async
         * @function fetchContractAccess
         * @returns {Promise<void>} Resolves when the contract access data has been fetched and applied to component state.
         */
        async fetchContractAccess() {
            try {
                const response = await apiClient.get(
                    `/api/pdf/get-contract-access/${this.client.id}/`
                );
                const data = response?.data;

                // Check if contract access doesn't exist (handled gracefully by backend)
                if (data?.status === "not_found") {
                    this.contractAccess = null;
                    this.contractValue = null;
                    this.originalContractValue = null;
                    return;
                }
                if (response && response.data) {
                    this.contractAccess = response.data;
                    this.contractValue = this.contractAccess.contract_value;
                    this.originalContractValue =
                        this.contractAccess.contract_value;

                    if (this.contractAccess.is_sent) {
                        this.$emit("is-contract-sent", true);
                    }
                    //  If the contract is signed, update status and store details

                    if (this.contractAccess.is_signed) {
                        // Convert the date to a human-readable format
                        const date = new Date(this.contractAccess.signed_at);
                        const formattedDate = date.toLocaleString("ro-RO", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                        });

                        // Create the formatted string
                        const signedInfo = `Semnat la: ${formattedDate} IP: ${this.contractAccess.signed_ip}`;

                        this.signatureToolTip = signedInfo;
                        this.signatureInfo = "Contract Semnat";
                        const emittedSignature = this.signatureInfo;
                        // Emit data to parent
                        this.$emit("signature-info", {
                            emittedSignature,
                            signedInfo,
                        });
                    }
                } else {
                    this.contractAccess = null;
                    this.contractValue = null;
                    this.originalContractValue = null;
                }
            } catch (error) {
                // Only log and alert for unexpected server/network errors
                console.error("Error fetching ContractAccess data:", error);
                showAlertModal(
                    this.$store,
                    "Eroare la obținerea contractului. Încercați din nou.",
                    "danger",
                    5000
                );
            }
        },
        /**
         * Saves or updates the contract value for the current client.
         * If the contract access record exists, it patches the existing record.
         * Otherwise, it creates a new one.
         *
         * @async
         * @function saveContractValue
         * @returns {Promise<void>} Resolves after the contract value is successfully saved or updated in the backend.
         */
        async saveContractValue() {
            if (!this.contractValue) {
                showAlertModal(
                    this.$store,
                    "Valoarea contractului este obligatorie!",
                    "warning",
                    5000
                );
                return;
            }

            try {
                this.isLoading = true;

                if (this.contractAccess?.id) {
                    // Update existing ContractAccess
                    const response = await apiClient.patch(
                        `/api/pdf/update-contract-access/${this.client.id}/`,
                        {
                            contract_value: this.contractValue,
                        }
                    );

                    if (response.status === 200) {
                        this.originalContractValue = this.contractValue;
                        showAlertModal(
                            this.$store,
                            "Valoarea contractului a fost actualizată!",
                            "success",
                            5000
                        );
                    }
                } else {
                    // Create new ContractAccess
                    const response = await apiClient.post(
                        `/api/pdf/update-contract-access/${this.client.id}/`,
                        {
                            contract_value: this.contractValue,
                        }
                    );

                    if (response.status === 201) {
                        this.contractAccess = response.data;
                        this.originalContractValue = this.contractValue;
                        showAlertModal(
                            this.$store,
                            "Valoarea contractului a fost salvată!",
                            "success",
                            5000
                        );
                    }
                }

                this.isLoading = false;
            } catch (error) {
                this.isLoading = false;
                console.error("Error saving contract value:", error);
                showAlertModal(
                    this.$store,
                    "A apărut o eroare la salvarea valorii contractului.",
                    "danger",
                    5000
                );
            }
        },
        /**
         * Validates the contract value (valoare contract) to ensure it is a non-negative number.
         * Resets the contract value to null if it is invalid (NaN or negative).
         *
         * @function validateValoareContract
         * @returns {void} Does not return anything; it updates local component state directly.
         */
        validateValoareContract() {
            if (this.contractValue < 0) {
                this.contractValue = 0;
            } else if (!this.contractValue || isNaN(this.contractValue)) {
                showAlertModal(
                    this.$store,
                    "Valoarea contractului trebuie să fie un număr valid!",
                    "warning",
                    5000
                );
                this.contractValue = null; // Reset if invalid
            }
        },
        /**
         * Generates a PDF for the contract and opens it in a new tab.
         * Constructs the URL for the PDF and opens it directly using the createPDFContract helper function.
         *
         * The method makes use of the createPDFContract function, passing the client ID to generate and display the contract.
         * In case of an error during the PDF generation, it logs the error to the console.
         */
        async generatePDFContract() {
            if (this.contractAccess?.is_signed) {
                let url = `${process.env.VUE_APP_API_BASE_URL}/media/${this.contractAccess.contract_path}`;
                window.open(url, "_blank");
                return;
            }
            if (!this.contractValue) {
                showAlertModal(
                    this.$store,
                    "Vă rugăm introduceți o valoare pentru contract!",
                    "warning",
                    5000
                );
                return;
            }

            // Save contract value if modified
            if (this.contractValue !== this.originalContractValue) {
                await this.saveContractValue();
            }

            try {
                await createPDFContract(this.client.id, 3, this.contractValue);
            } catch (error) {
                showAlertModal(
                    this.$store,
                    "A apărut o eroare la generarea contractului.",
                    "danger",
                    12000,
                    error
                );
            }
        },
        /**
         * Sends an email with a link to access the contract to the client.
         * The email includes a link to the contract, which the client can access after entering an OTP
         * that will be sent separately via SMS when they attempt to sign the contract.
         * If successful, it displays a success modal to the user. In case of errors, it displays an error modal.
         *
         * Sends an API request to the backend to:
         * - Generate a new contract PDF.
         * - Delete any previous contract records for this client.
         * - Send an email to the client's email address with a link to view the contract.
         *
         * @async
         * @function sendEmail
         * @returns {Promise<void>} Displays a success or error alert modal based on the response status.
         */
        async sendEmail() {
            // Save contract value if modified
            if (this.contractValue !== this.originalContractValue) {
                await this.saveContractValue();
            }
            if (!this.contractValue) {
                showAlertModal(
                    this.$store,
                    "Vă rugăm introduceți o valoare pentru contract!",
                    "warning",
                    5000
                );
                return;
            }

            try {
                this.isLoading = true;
                const response = await apiClient.post("/api/pdf/send-email/", {
                    phoneNumber: this.client.phone,
                    addtionalPhone: this.client.additional_phone,
                    email: this.client.email,
                    additionalEmail: this.client.additional_email,
                    client_id: this.client.id,
                    document_type: 3,
                    contract_value: this.contractValue,
                    message:
                        "Your contract has been generated and sent to your email.",
                });

                if (response.status === 200) {
                    this.isLoading = false;
                    this.emitUpdatedStatus("Trimis");

                    showAlertModal(
                        this.$store,
                        "Contractul a fost trimis pe mail-ul clientului, iar agentul a fost notificat prin email.",
                        "success",
                        5000
                    );
                } else {
                    this.isLoading = false;
                    showAlertModal(
                        this.$store,
                        "A apărut o eroare! Vă rugăm reîncercați!",
                        "danger",
                        12000
                    );
                }
            } catch (error) {
                this.isLoading = false;
                showAlertModal(
                    this.$store,
                    "A apărut o eroare la trimiterea mail-ului! Vă rugăm să încercați din nou după reîmprospătarea paginii!",
                    "danger",
                    12000,
                    error
                );
            }
        },
    },
    watch: {
        // once we know the contract is signed, start our base‐line snapshot
        "contractAccess.is_signed": {
            handler(signed) {
                if (signed) {
                    this.actSnapshot = JSON.parse(JSON.stringify(this.client));
                    this.pendingDiffs = {};
                }
            },
            immediate: true,
        },
        client: {
            deep: true,
            handler() {
                if (!this.contractAccess?.is_signed) return;

                /* what is different _right now_ ? */
                let diffs = this.getDifferences(
                    this.originalClient,
                    this.client
                );

                // ── remove keys we don’t want to track ───────────────────────────
                this.IGNORED_DIFF_KEYS.forEach((k) => delete diffs[k]);

                /* find (or lazily create) the working draft */
                let draft = this.additionalActs.find((a) => !a.is_sent);
                if (!draft && Object.keys(diffs).length) {
                    draft = {
                        id: null,
                        modifications: [],
                        autoDiffs: {},
                        user_note: "",
                        is_sent: false,
                    };
                    showAlertModal(
                        this.$store,
                        "A fost adăugat un act adițional!",
                        "warning",
                        5000
                    );
                    this.additionalActs.unshift(draft);
                    this.pendingAdds.push(draft);
                    this.showAdditionalActsManager = true;
                    this.contractExpanded = [0];
                }

                /* ----------  A) add new diffs  ---------- */
                if (draft && Object.keys(diffs).length) {
                    draft.autoDiffs = { ...draft.autoDiffs, ...diffs };
                    Object.assign(this.pendingDiffs, diffs);
                }
            },
        },
    },
};
</script>
